import { loadMoonPay } from '@js-sdk';
import {
  type ReactNode,
  createContext,
  useCallback,
  useEffect,
  useRef,
  type MutableRefObject,
} from 'react';
import { getEnvironmentFromApiKey } from '@common';
import type { MoonPayInitFn, MoonPayWebSdkConfig } from '../types';

interface MoonPayProviderProps {
  children: ReactNode;
  apiKey: string;
  debug?: boolean;
}

type MoonPayContext = Omit<MoonPayProviderProps, 'children'> & {
  init: MutableRefObject<MoonPayInitFn | null>;
  environment: MoonPayWebSdkConfig['environment'];
};

export const MoonPayContext = createContext<MoonPayContext>({
  init: { current: null },
  apiKey: '',
  environment: 'sandbox',
  debug: false,
});

export const MoonPayProvider = ({
  children,
  apiKey,
  debug = false,
}: MoonPayProviderProps) => {
  const environment = getEnvironmentFromApiKey(apiKey);
  const init = useRef<MoonPayInitFn | null>(null);

  const load = useCallback(async () => {
    const moonPay = await loadMoonPay();
    init.current = moonPay ?? null;
  }, []);

  useEffect(() => {
    if (!init.current) load();
  }, [load]);

  return (
    <MoonPayContext.Provider
      value={{
        init,
        apiKey,
        environment,
        debug,
      }}
    >
      {children}
    </MoonPayContext.Provider>
  );
};
